/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from '@emotion/styled';
import React, { useRef, useState } from 'react';

import { useUploadFile } from 'components/MenuMakeImport/hooks';
import { CircularProgress } from '@mui/material';
/**
 * Drag and Drop visual component for importing templates
 *
 * @returns {JSX.Element} Drag and drop area component
 *
 * @example
 * <DragAndDrop />
 */
interface DragAndDropProps {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
  title: string;
  description: string;
  img: React.ReactNode;
}

export const DragAndDrop = ({ onSuccess, onError, title, description, img }: DragAndDropProps) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: mutateUploadFile, isLoading: isUploadingFile } = useUploadFile();

  const handleFile = async (fileDrag: File) => {
    const formData = new FormData();
    formData.append('importedFile', fileDrag);
    mutateUploadFile(
      {
        body: formData,
      },
      {
        onError: (error: any) => {
          if (error?.code === 'menuTemplate.InvalidFile') {
            setIsError(true);
            onError(error?.errors);
          } else {
          }
        },
        onSuccess: () => {
          setIsSuccess(true);
          onSuccess(fileDrag);
        },
      },
    );
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    setIsError(false);
    const fileDrag = e.dataTransfer.files[0];
    if (fileDrag) handleFile(fileDrag);
  };

  const onDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileDrag = e.target.files?.[0];
    if (fileDrag) handleFile(fileDrag);
    e.target.value = '';
  };

  return (
    <DropArea
      aria-label="Importar menú"
      htmlFor="file-upload"
      isDragActive={isDragActive}
      isError={isError}
      isSuccess={isSuccess}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      role="button"
      tabIndex={0}
    >
      {isUploadingFile && (
        <LoadingOverlay>
          <CircularProgress color="secondary" size={42} thickness={6} />
        </LoadingOverlay>
      )}
      <SVGWrapper>{img}</SVGWrapper>
      <TextContainer>
        <Title>{title}</Title>
        <Subtitle>{description}</Subtitle>
      </TextContainer>
      <HiddenInput accept=".xlsx" id="file-upload" onChange={onChange} ref={inputRef} type="file" />
    </DropArea>
  );
};

const DropArea = styled.label<{ isDragActive: boolean; isError: boolean; isSuccess: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #6b6b7b;
  border-radius: 16px;
  min-height: 250px;
  width: 100%;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.2s, background 0.2s;
  outline: none;
  position: relative;

  ${(props) =>
    props.isDragActive
      ? `
    border: 2px dashed #6b6b7b !important;
    &::before {

      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 16px;
      z-index: 1;
    }
  `
      : ''}
  ${(props) => (props.isError ? 'border-color: #FF5A5F;' : '')}
  ${(props) => (props.isSuccess ? 'border-color: #27AE60;' : '')}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SVGWrapper = styled.div`
  vertical-align: top;
  margin-bottom: 24px;
  svg {
    vertical-align: top;
  }
`;

const TextContainer = styled.div`
  text-align: center;
`;

const Title = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  color: #47475a;
  margin-bottom: 8px;
`;

const Subtitle = styled.div`
  font-size: 0.85rem;
  color: #6b6b7b;
  font-weight: 400;
`;

const HiddenInput = styled.input`
  display: none;
`;
